// Custom hook for managing prompt filtering state
import { useState, useMemo } from 'react';
import { PromptFilterService, FilterOptions, FilterStats } from '../services/promptFilterService';
import { PromptData } from './useProjectData';

export const usePromptFilters = (prompts: PromptData[]) => {
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    contentType: 'all',
    status: 'all', 
    sortBy: 'date-desc'
  });

  // Memoized filtered prompts
  const filteredPrompts = useMemo(() => {
    return PromptFilterService.applyFilters(prompts, filterOptions);
  }, [prompts, filterOptions]);

  // Memoized filter statistics (context-aware: changes based on selected content type)
  const filterStats = useMemo(() => {
    return PromptFilterService.getContextualFilterStats(prompts, filterOptions.contentType);
  }, [prompts, filterOptions.contentType]);

  // Update content type filter
  const setContentTypeFilter = (contentType: FilterOptions['contentType']) => {
    setFilterOptions(prev => ({ ...prev, contentType }));
  };

  // Update status filter
  const setStatusFilter = (status: FilterOptions['status']) => {
    setFilterOptions(prev => ({ ...prev, status }));
  };

  // Update sort order
  const setSortOrder = (sortBy: FilterOptions['sortBy']) => {
    setFilterOptions(prev => ({ ...prev, sortBy }));
  };

  // Reset all filters
  const resetFilters = () => {
    setFilterOptions({
      contentType: 'all',
      status: 'all',
      sortBy: 'date-desc'
    });
  };

  return {
    filteredPrompts,
    filterStats,
    filterOptions,
    setContentTypeFilter,
    setStatusFilter,
    setSortOrder,
    resetFilters
  };
};
